/**
 * useEnumTranslation Composable
 *
 * Centralised enum-to-label translation for all domain enums that come
 * from API responses or are used as fixed option lists (statuses, roles,
 * milk types, etc.).
 *
 * Internally delegates to the generic `createEnumTranslation` factory from
 * `@tms-phoenix/ui-vue`, passing in the app's i18next `t` function and
 * reactive `locale` ref. The factory builds reactive computed label maps,
 * getter functions, and dropdown-ready option arrays for each enum group.
 *
 * ### Usage
 *
 * ```vue
 * <script setup lang="ts">
 * const { getUserRoleLabel, getPatientStatusLabel, userRoleOptions } = useEnumTranslation();
 * </script>
 *
 * <template>
 *   <!-- Single value lookup -->
 *   <span>{{ getUserRoleLabel('NURSE') }}</span>
 *
 *   <!-- Dropdown / filter options -->
 *   <Select :options="userRoleOptions" />
 * </template>
 * ```
 *
 * ### Adding a new enum
 *
 * 1. Add i18n keys to `packages/tms-types/src/locales/en.json`
 *    following the pattern `{domain}.{enumName}{PascalValue}`.
 * 2. Add an entry to the `enumConfig` object below.
 * 3. Wire the new group properties in the return statement.
 */
import { useTranslation } from 'i18next-vue';
import { createEnumTranslation } from '@tms-phoenix/ui-vue';
import { useLocale } from './useLocale';

// Re-export the shared type so existing imports keep working
export type { EnumOption } from '@tms-phoenix/ui-vue';

/**
 * Enum group configuration — declarative mapping of every domain enum
 * to its i18n keys. The `createEnumTranslation` factory from `@tms-phoenix/ui-vue`
 * turns this into reactive computed labels, getLabel helpers, and option arrays.
 */
const enumConfig = {
  // ── User Roles ──────────────────────────────────────────────────────────
  userRole: {
    entries: [
      { value: 'TECHNICAL_ADMIN', key: 'user.roleTechnicalAdmin' },
      { value: 'CLINICAL_ADMIN', key: 'user.roleClinicalAdmin' },
      { value: 'NICU_MANAGER', key: 'user.roleNicuManager' },
      { value: 'NURSE', key: 'user.roleNurse' },
      { value: 'PREP_TECHNICIAN', key: 'user.rolePrepTechnician' },
    ],
  },
  // ── User Statuses ───────────────────────────────────────────────────────
  userStatus: {
    entries: [
      { value: 'CONFIRMED', key: 'user.statusConfirmed' },
      { value: 'DISABLED', key: 'ui.userTable.statusDisabled' },
      { value: 'EXTERNAL_PROVIDER', key: 'user.statusExternalProvider' },
      { value: 'FORCE_CHANGE_PASSWORD', key: 'user.statusForceChangePassword' },
      { value: 'RESET_REQUIRED', key: 'user.statusResetRequired' },
      { value: 'UNCONFIRMED', key: 'user.statusUnconfirmed' },
      { value: 'UNKNOWN', key: 'common.unknown' },
    ],
  },
  // ── Patient Statuses ────────────────────────────────────────────────────
  patientStatus: {
    entries: [
      { value: 'STABLE', key: 'patient.status.stable' },
      { value: 'SERIOUS', key: 'patient.status.serious' },
      { value: 'CRITICAL', key: 'patient.status.critical' },
      { value: 'LIFE_THREATENING', key: 'patient.status.lifeThreatening' },
    ],
  },
  // ── Milk Types ──────────────────────────────────────────────────────────
  milkType: {
    entries: [
      { value: 'PRE_TERM', key: 'inventory.milkTypePreterm' },
      { value: 'TERM', key: 'inventory.milkTypeTerm' },
      { value: 'FOREMILK', key: 'inventory.milkTypeForemilk' },
      { value: 'HINDMILK', key: 'inventory.milkTypeHindmilk' },
      { value: 'SKIM', key: 'inventory.milkTypeSkimHumanMilk' },
      { value: 'COLOSTRUM', key: 'inventory.milkTypeColostrum' },
    ],
  },
  // ── Milk Statuses ───────────────────────────────────────────────────────
  milkStatus: {
    entries: [
      { value: 'FRESH', key: 'inventory.milkStatusFresh' },
      { value: 'FROZEN', key: 'inventory.milkStatusFrozen' },
      { value: 'THAWED', key: 'inventory.milkStatusThawed' },
    ],
  },  // ── Milk Storage Type (Milk Formula Storage settings step) ──────────────
  milkStorageType: {
    entries: [
      { value: 'REFRIGERATOR', key: 'settings.milkFormulaStorage.milkStorageTypeRefrigerator' },
      { value: 'FREEZER', key: 'settings.expiryRules.locationFreezer' },
      { value: 'DEEP_FREEZER', key: 'settings.expiryRules.locationDeepFreezer' },
    ],
  },
  // ── Formula Storage Type (Milk Formula Storage settings step) ─────────────
  formulaStorageType: {
    entries: [
      { value: 'ROOM_TEMPERATURE', key: 'settings.milkFormulaStorage.formulaStorageTypeRoomTemperature' },
      { value: 'REFRIGERATOR', key: 'settings.milkFormulaStorage.milkStorageTypeRefrigerator' },
    ],
  },  // ── Donor Approval Statuses ─────────────────────────────────────────────
  donorStatus: {
    entries: [
      { value: 'Pending', key: 'common.pending' },
      { value: 'Cleared', key: 'donor.cleared' },
      { value: 'Approved', key: 'milk.actionApproved' },
      { value: 'Rejected', key: 'donor.rejected' },
      { value: 'Expired', key: 'common.expired' },
      { value: 'On Hold', key: 'feed.statusOnHold' },
    ],
  },
  // ── Donor Status Filter (subset used in inventory filter dropdown) ───────
  donorStatusFilter: {
    entries: [
      { value: 'Approved', key: 'milk.actionApproved' },
      { value: 'Cleared', key: 'donor.cleared' },
      { value: 'On Hold', key: 'feed.statusOnHold' },
    ],
  },
  // ── Feed Order Types ────────────────────────────────────────────────────
  feedOrderType: {
    entries: [
      { value: 'Standard', key: 'feed.typeStandard' },
      { value: 'NPO', key: 'feed.typeNpo' },
      { value: 'Oral Care', key: 'feed.typeOralCare' },
    ],
  },
  // ── Feed Statuses (full set for labels / getLabel) ──────────────────────
  feedStatus: {
    entries: [
      { value: 'DELIVERED', key: 'feed.statusDelivered' },
      { value: 'PENDING', key: 'common.pending' },
      { value: 'ACTIVE', key: 'common.active' },
      { value: 'INACTIVE', key: 'common.inactive' },
      { value: 'COMPLETED', key: 'feed.statusCompleted' },
      { value: 'CANCELLED', key: 'feed.statusCancelled' },
      { value: 'ON_HOLD', key: 'feed.statusOnHold' },
    ],
  },
  // ── Feed Status Filter (subset used in filter dropdowns) ────────────────
  feedStatusFilter: {
    entries: [
      { value: 'OVERDUE', key: 'feed.statusOverdue' },
      { value: 'DUE_SOON', key: 'feed.statusDueSoon' },
      { value: 'ON_TIME', key: 'feed.statusOnTime' },
      { value: 'PENDING', key: 'common.pending' },
    ],
  },
  // ── Milk Origin ─────────────────────────────────────────────────────────
  milkOrigin: {
    entries: [
      { value: 'mother', key: 'common.mother' },
      { value: 'donor', key: 'common.donor' },
    ],
  },
  // ── Child Status (Mother) ───────────────────────────────────────────────
  childStatus: {
    entries: [
      { value: 'fullTerm', key: 'mother.childStatus.fullTerm' },
      { value: 'premature', key: 'mother.childStatus.premature' },
      { value: 'deceased', key: 'mother.childStatus.deceased' },
      { value: 'unknown', key: 'common.unknown' },
    ],
  },
  // ── Alcohol Use (Mother) ────────────────────────────────────────────────
  alcoholUse: {
    entries: [
      { value: 'none', key: 'common.none' },
      { value: 'occasional', key: 'mother.alcohol.occasional' },
      { value: 'moderate', key: 'mother.alcohol.moderate' },
      { value: 'regular', key: 'mother.alcohol.regular' },
      { value: 'unknown', key: 'common.unknown' },
    ],
  },
  // ── Diet Type (Mother) ──────────────────────────────────────────────────
  dietType: {
    entries: [
      { value: 'vegetarian', key: 'mother.diet.vegetarian' },
      { value: 'vegan', key: 'mother.diet.vegan' },
      { value: 'omnivore', key: 'mother.diet.omnivore' },
      { value: 'halal', key: 'ui.nutritions.halal' },
      { value: 'kosher', key: 'ui.nutritions.kosher' },
      { value: 'restricted', key: 'mother.diet.restricted' },
      { value: 'other', key: 'common.other' },
    ],
  },
  // ── Mother Role ─────────────────────────────────────────────────────────
  motherRole: {
    entries: [
      { value: 'MOTHER', key: 'common.mother' },
      { value: 'BOTH', key: 'mother.motherAndDonor' },
    ],
  },
  // ── Donor Role ──────────────────────────────────────────────────────────
  donorRole: {
    entries: [
      { value: 'DONOR', key: 'common.donor' },
      { value: 'BOTH', key: 'mother.motherAndDonor' },
    ],
  },
  // ── Donor Approval Filter Status ────────────────────────────────────────
  donorApprovalFilter: {
    entries: [
      { value: 'approved', key: 'donor.approvedDonor' },
      { value: 'on-hold', key: 'donor.status.onHold' },
      { value: 'pending', key: 'donor.pendingApproval' },
    ],
  },
  // ── Milk Batch Status ───────────────────────────────────────────────────
  milkBatchStatus: {
    entries: [
      { value: 'Unapproved', key: 'milk.statusUnapproved' },
      { value: 'Approved', key: 'milk.actionApproved' },
      { value: 'Retest', key: 'milk.actionRetest' },
      { value: 'Failed', key: 'user.failed' },
      { value: 'Rejected', key: 'donor.rejected' },
    ],
  },
  // ── Milk Batch Status Filter (excludes Failed) ──────────────────────────
  milkBatchStatusFilter: {
    entries: [
      { value: 'Unapproved', key: 'milk.statusUnapproved' },
      { value: 'Approved', key: 'milk.actionApproved' },
      { value: 'Retest', key: 'milk.actionRetest' },
      { value: 'Rejected', key: 'donor.rejected' },
    ],
  },
  // ── Milk Pool / Pasteurization Status ───────────────────────────────────
  // 'Failed' removed: pasteurization is now driven by a controlled
  // temperature/hold-time dropdown, so a pool can never be marked Failed.
  milkPoolStatus: {
    entries: [
      { value: 'Prep', key: 'milk.statusPrep' },
      { value: 'Quarantined', key: 'milk.statusQuarantined' },
      { value: 'Completed', key: 'feed.statusCompleted' },
    ],
  },
  // ── Batch Approval Action ───────────────────────────────────────────────
  batchApprovalAction: {
    entries: [
      { value: 'Approved', key: 'milk.actionApproved' },
      { value: 'Retest', key: 'milk.actionRetest' },
      { value: 'Reject', key: 'common.reject' },
    ],
  },
  // ── Product Status (Inventory) ──────────────────────────────────────────
  productStatus: {
    entries: [
      { value: 'Active', key: 'common.active' },
      { value: 'Recall', key: 'feed.status.recall' },
    ],
  },
  // ── Recall Status ───────────────────────────────────────────────────────
  recallStatus: {
    entries: [
      { value: 'Pending', key: 'common.pending' },
      { value: 'Approved', key: 'milk.actionApproved' },
      { value: 'Rejected', key: 'donor.rejected' },
      { value: 'Cancelled', key: 'feed.statusCancelled' },
    ],
  },
  // ── Recall Relationship ─────────────────────────────────────────────────
  relationship: {
    entries: [
      { value: 'Mother', key: 'common.mother' },
      { value: 'Father', key: 'recall.communication.relationship.father' },
      { value: 'Guardian', key: 'recall.communication.relationship.guardian' },
      { value: 'Other', key: 'common.other' },
    ],
  },
  // ── Sex Options ─────────────────────────────────────────────────────────
  sex: {
    entries: [
      { value: 'Male', key: 'patient.sex.male' },
      { value: 'Female', key: 'patient.sex.female' },
      { value: 'Undetermined', key: 'patient.sex.undetermined' },
    ],
  },
  // ── Patient Communication Channel (shared with recall communication method) ─
  patientCommunicationChannel: {
    entries: [
      { value: 'PHONE', key: 'common.phone' },
      { value: 'IN_PERSON', key: 'recall.communication.method.inPerson' },
      { value: 'EMAIL', key: 'common.email' },
      { value: 'IN_APP', key: 'recall.communication.method.portalMessage' },
      { value: 'WRITTEN', key: 'recall.communication.method.letterMail' },
      { value: 'SMS', key: 'recall.communication.method.sms' },
    ],
  },
  // ── Patient Communication Type ───────────────────────────────────────────
  patientCommunicationType: {
    entries: [
      { value: 'RECALL_NOTICE', key: 'patient.communication.type.recallNotice' },
      { value: 'CLINICAL_UPDATE', key: 'patient.communication.type.clinicalUpdate' },
      { value: 'CONSENT_REQUEST', key: 'patient.communication.type.consentRequest' },
      { value: 'DISCHARGE_SUMMARY', key: 'patient.communication.type.dischargeSummary' },
      { value: 'GENERAL', key: 'patient.communication.type.general' },
    ],
  },
  // ── Patient Communication Recipient Type ────────────────────────────────
  patientCommunicationRecipientType: {
    entries: [
      { value: 'PARENT', key: 'patient.communication.recipientType.parent' },
      { value: 'GUARDIAN', key: 'recall.communication.relationship.guardian' },
      { value: 'NEXT_OF_KIN', key: 'patient.communication.recipientType.nextOfKin' },
      { value: 'CLINICAL_STAFF', key: 'patient.communication.recipientType.clinicalStaff' },
    ],
  },
  // ── Patient Communication Status ────────────────────────────────────────
  patientCommunicationStatus: {
    entries: [
      { value: 'PENDING', key: 'common.pending' },
      { value: 'SENT', key: 'patient.communication.status.sent' },
      { value: 'DELIVERED', key: 'feed.statusDelivered' },
      { value: 'ACKNOWLEDGED', key: 'patient.communication.status.acknowledged' },
      { value: 'FAILED', key: 'user.failed' },
      { value: 'NO_RESPONSE', key: 'patient.communication.status.noResponse' },
    ],
  },
  // ── Allergen Severity ───────────────────────────────────────────────────
  allergenSeverity: {
    entries: [
      { value: 'Mild', key: 'settings.allergyManagement.severityMild' },
      { value: 'Moderate', key: 'mother.alcohol.moderate' },
      { value: 'High', key: 'settings.allergyManagement.severityHigh' },
      { value: 'Severe', key: 'settings.allergyManagement.severitySevere' },
    ],
  },
  // ── Allergen Actions ────────────────────────────────────────────────────
  allergenAction: {
    entries: [
      { value: 'No Action', key: 'settings.allergyManagement.actionNoAction' },
      { value: 'Hard Stop (Stop)', key: 'settings.allergyManagement.actionHardStop' },
      { value: 'Soft Stop (Warning)', key: 'settings.allergyManagement.actionSoftStop' },
    ],
  },
  // ── Milk Type Status ────────────────────────────────────────────────────
  milkTypeStatus: {
    entries: [
      { value: 'Active', key: 'common.active' },
      { value: 'Inactive', key: 'common.inactive' },
    ],
  },
  // ── Facility Type ───────────────────────────────────────────────────────
  facilityType: {
    entries: [
      { value: "Children's Hospital", key: 'settings.hospitalConfig.facilityTypeChildrensHospital' },
      { value: 'Pediatric Hospital', key: 'settings.hospitalConfig.facilityTypePediatricHospital' },
      { value: "Women and Children's Hospital", key: 'settings.hospitalConfig.facilityTypeWomenAndChildrensHospital' },
      { value: 'Outpatient Pediatric Clinic', key: 'settings.hospitalConfig.facilityTypeOutpatientPediatricClinic' },
      { value: 'Adult Care Hospital', key: 'settings.hospitalConfig.facilityTypeAdultCareHospital' },
      { value: 'General Hospital', key: 'settings.hospitalConfig.facilityTypeGeneralHospital' },
      { value: 'General Acute Care Hospital', key: 'settings.hospitalConfig.facilityTypeGeneralAcuteCareHospital' },
    ],
  },
  // ── Care Unit Type ──────────────────────────────────────────────────────
  careUnitType: {
    entries: [
      { value: 'NICU', key: 'settings.hospitalConfig.unitTypeNicu' },
      { value: 'PICU', key: 'settings.hospitalConfig.unitTypePicu' },
      { value: 'CVICU', key: 'settings.hospitalConfig.unitTypeCvicu' },
      { value: 'ICU', key: 'settings.hospitalConfig.unitTypeIcu' },
      { value: 'Pediatric Cardiothoracic ICU', key: 'settings.hospitalConfig.unitTypePediatricCardiothoracicIcu' },
      { value: 'Pediatric Step Down Unit', key: 'settings.hospitalConfig.unitTypePediatricStepDownUnit' },
      { value: 'Immediate Care Unit', key: 'settings.hospitalConfig.unitTypeImmediateCareUnit' },
      { value: 'General Pediatric Unit', key: 'settings.hospitalConfig.unitTypeGeneralPediatricUnit' },
      { value: 'Labor and Delivery', key: 'settings.hospitalConfig.unitTypeLaborAndDelivery' },
      { value: 'Mother-Baby Unit', key: 'settings.hospitalConfig.unitTypeMotherBabyUnit' },
      { value: 'Postpartum Unit', key: 'settings.hospitalConfig.unitTypePostpartumUnit' },
      { value: 'Special Care Nursery', key: 'settings.hospitalConfig.unitTypeSpecialCareNursery' },
      { value: 'Well-Baby Nursery', key: 'settings.hospitalConfig.unitTypeWellBabyNursery' },
    ],
  },
  // ── Milk Category (Expiry Rules) ────────────────────────────────────────
  milkCategory: {
    entries: [
      { value: 'Mother Milk', key: 'disposeArea.option.motherMilk' },
      { value: 'Donor Milk', key: 'milk.donorMilk' },
      { value: 'Donor Pasteurised Milk', key: 'settings.expiryRules.categoryDonorPasteurisedMilk' },
      { value: 'Formula', key: 'patient.milkLog.milkTypeFormula' },
      { value: 'Feed', key: 'printLabel.feedTypeValue' },
    ],
  },
  // ── Milk State / "Type" column (Expiry Rules) ───────────────────────────
  milkState: {
    entries: [
      { value: 'Fresh', key: 'inventory.milkStatusFresh' },
      { value: 'Thawed', key: 'inventory.milkStatusThawed' },
      { value: 'Frozen', key: 'inventory.milkStatusFrozen' },
      { value: 'Pasteurised', key: 'settings.expiryRules.milkStatePasteurised' },
      { value: 'Open', key: 'common.open' },
    ],
  },
  // ── Storage State / "State" column (Expiry Rules — Milk table) ─────────
  expiryStorageState: {
    entries: [
      { value: 'REFRIGERATED', key: 'settings.expiryRules.stateRefrigerated' },
      { value: 'Frozen', key: 'inventory.milkStatusFrozen' },
      { value: 'Deep Frozen', key: 'settings.expiryRules.stateDeepFrozen' },
      { value: 'Thawed', key: 'inventory.milkStatusThawed' },
    ],
  },
  // ── Physical Location / "Location" column (Expiry Rules — Milk + Feed) ──
  expiryLocation: {
    entries: [
      { value: 'Fridge', key: 'settings.expiryRules.locationFridge' },
      { value: 'Freezer', key: 'settings.expiryRules.locationFreezer' },
      { value: 'Deep Freezer', key: 'settings.expiryRules.locationDeepFreezer' },
    ],
  },
  // ── Device Type (Printer Config settings) ───────────────────────────────
  deviceType: {
    entries: [
      { value: 'printer', key: 'settings.printerConfig.colPrinter' },
      { value: 'scanner', key: 'settings.printerConfig.colScanner' },
    ],
  },
  // ── Device Status (Printer Config settings — discovered + configured) ──
  deviceStatus: {
    entries: [
      { value: 'online', key: 'settings.printerConfig.statusOnline' },
      { value: 'active', key: 'common.active' },
      { value: 'healthy', key: 'settings.printerConfig.statusHealthy' },
      { value: 'discovered', key: 'settings.printerConfig.statusDiscovered' },
      { value: 'ready', key: 'settings.printerConfig.statusReady' },
      { value: 'offline', key: 'settings.printerConfig.statusOffline' },
      { value: 'error', key: 'toast.error' },
      { value: 'failed', key: 'user.failed' },
      { value: 'disconnected', key: 'settings.printerConfig.statusDisconnected' },
    ],
  },
  // ── Formula Form Factor / "Form Factor" column (Expiry Rules — Formula) ─
  // Full 8-value PRODUCT_FORM_TYPES enum (products.schema.ts) + "Human Milk
  // Fortifier" (a formFactor-only value not part of the product catalog enum).
  // Values below must match PRODUCT_FORM_TYPES casing exactly ("Ready to
  // Feed"/"Ready to Hang", lowercase "to") — getLabel/options do case-
  // sensitive matching with no normalization, so a mismatch here silently
  // falls back to the raw untranslated string instead of the label.
  formulaFormFactor: {
    entries: [
      { value: 'Powder', key: 'settings.expiryRules.formFactorPowder' },
      { value: 'Human Milk Fortifier', key: 'settings.expiryRules.formFactorHmf' },
      { value: 'Concentrate', key: 'settings.expiryRules.formFactorConcentrate' },
      { value: 'Ready to Feed', key: 'settings.expiryRules.formFactorReadyToFeed' },
      { value: 'Ready to Hang', key: 'settings.expiryRules.formFactorReadyToHang' },
      { value: 'Sterile Water', key: 'settings.expiryRules.formFactorSterileWater' },
      { value: 'Donor Milk', key: 'milk.donorMilk' },
      { value: 'Food Blend', key: 'disposeArea.formType.foodBlend' },
      { value: 'Human Milk', key: 'disposeArea.formType.humanMilk' },
    ],
  },
  // ── Product Form / form_name (product catalog) ──────────────────────────
  // The exact 8-value PRODUCT_FORM_TYPES enum (products.schema.ts) — for
  // translating the raw `form_name` field as it comes back from the product
  // catalog / feed-order / traceability APIs (no "Human Milk Fortifier",
  // unlike formulaFormFactor above, since that value is not a valid form_name).
  productForm: {
    entries: [
      { value: 'Concentrate', key: 'settings.expiryRules.formFactorConcentrate' },
      { value: 'Donor Milk', key: 'milk.donorMilk' },
      { value: 'Food Blend', key: 'disposeArea.formType.foodBlend' },
      { value: 'Human Milk', key: 'disposeArea.formType.humanMilk' },
      { value: 'Powder', key: 'settings.expiryRules.formFactorPowder' },
      { value: 'Ready to Feed', key: 'settings.expiryRules.formFactorReadyToFeed' },
      { value: 'Ready to Hang', key: 'settings.expiryRules.formFactorReadyToHang' },
      { value: 'Sterile Water', key: 'settings.expiryRules.formFactorSterileWater' },
    ],
  },
  // ── Milk Storage Condition (Expiry Rules — Milk feed-type column) ───────
  milkStorageCondition: {
    entries: [
      { value: 'Plain', key: 'settings.expiryRules.storagePlain' },
      { value: 'Mixed / Fortified', key: 'settings.expiryRules.storageMixedFortified' },
    ],
  },
  // ── Feed Expiry Type (Expiry Rules — Feed table feed-type column) ───────
  feedExpiryType: {
    entries: [
      { value: 'Plain', key: 'settings.expiryRules.storagePlain' },
      { value: 'Fortified', key: 'reports.annual.fortified' },
    ],
  },
  // ── Expiry Unit ─────────────────────────────────────────────────────────
  expiryUnit: {
    entries: [
      { value: 'hours', key: 'settings.expiryRules.unitHours' },
      { value: 'days', key: 'settings.expiryRules.unitDays' },
    ],
  },
  // ── Settings Role Names (for Roles & Permissions matrix) ────────────────
  // Covers every value in the backend's CognitoUserRoleSchema
  // (packages/tms-types/src/schemas/cognito-user.schema.ts) — the full set
  // of role codes that can be assigned to a hospital user, including the
  // control-plane roles (ADMIN, PRODUCTION_SUPPORT, PRODUCT_TEAM) and the
  // DEFAULT/DONOR system roles.
  settingsRole: {
    entries: [
      { value: 'NURSE', key: 'user.roleNurse' },
      { value: 'PREP_TECHNICIAN', key: 'user.rolePrepTechnician' },
      { value: 'NICU_MANAGER', key: 'user.roleNicuManager' },
      { value: 'CLINICAL_ADMIN', key: 'user.roleClinicalAdmin' },
      { value: 'TECHNICAL_ADMIN', key: 'user.roleTechnicalAdmin' },
      { value: 'ENTERPRISE_ADMIN', key: 'settings.rolesPermissions.roleEnterpriseAdmin' },
      { value: 'INVENTORY_MANAGER', key: 'settings.rolesPermissions.roleInventoryManager' },
      { value: 'INTAKE_STAFF', key: 'settings.rolesPermissions.roleIntakeStaff' },
      { value: 'MILK_BANK_OPERATOR', key: 'settings.rolesPermissions.roleMilkBankOperator' },
      { value: 'DONOR_COORDINATOR', key: 'settings.rolesPermissions.roleDonorCoordinator' },
      { value: 'DIETITIAN', key: 'patient.role.dietitian' },
      { value: 'LACTATION', key: 'settings.rolesPermissions.roleLactation' },
      { value: 'MILK_PREP_TECHNICIAN', key: 'settings.rolesPermissions.roleMilkPrepTechnician' },
      { value: 'UNIT_SECRETARY', key: 'settings.rolesPermissions.roleUnitSecretary' },
      { value: 'ANCILLARY_STAFF', key: 'settings.rolesPermissions.roleAncillaryStaff' },
      { value: 'AUDITOR', key: 'settings.rolesPermissions.roleAuditor' },
      { value: 'DEFAULT', key: 'settings.localization.colDefaultEn' },
      { value: 'DONOR', key: 'common.donor' },
      { value: 'ADMIN', key: 'settings.idpFederation.roleAdmin' },
      { value: 'PRODUCTION_SUPPORT', key: 'settings.rolesPermissions.roleProductionSupport' },
      { value: 'PRODUCT_TEAM', key: 'settings.rolesPermissions.roleProductTeam' },
    ],
  },
  // ── Settings Role Subtitles (for Roles & Permissions matrix) ────────────
  settingsRoleSubtitle: {
    entries: [
      { value: 'NURSE', key: 'settings.rolesPermissions.roleNurseSubtitle' },
      { value: 'PREP_TECHNICIAN', key: 'settings.rolesPermissions.rolePrepTechnicianSubtitle' },
      { value: 'NICU_MANAGER', key: 'settings.rolesPermissions.roleNicuManagerSubtitle' },
      { value: 'CLINICAL_ADMIN', key: 'settings.rolesPermissions.roleClinicalAdminSubtitle' },
      { value: 'TECHNICAL_ADMIN', key: 'settings.rolesPermissions.roleTechnicalAdminSubtitle' },
      { value: 'ENTERPRISE_ADMIN', key: 'settings.rolesPermissions.roleEnterpriseAdminSubtitle' },
      { value: 'INVENTORY_MANAGER', key: 'settings.rolesPermissions.roleInventoryManagerSubtitle' },
      { value: 'INTAKE_STAFF', key: 'settings.rolesPermissions.roleIntakeStaffSubtitle' },
      { value: 'MILK_BANK_OPERATOR', key: 'settings.rolesPermissions.roleMilkBankOperatorSubtitle' },
      { value: 'DONOR_COORDINATOR', key: 'settings.rolesPermissions.roleDonorCoordinatorSubtitle' },
      { value: 'DIETITIAN', key: 'settings.rolesPermissions.roleDietitianSubtitle' },
      { value: 'LACTATION', key: 'settings.rolesPermissions.roleLactationSubtitle' },
      { value: 'MILK_PREP_TECHNICIAN', key: 'settings.rolesPermissions.roleMilkPrepTechnicianSubtitle' },
      { value: 'UNIT_SECRETARY', key: 'settings.rolesPermissions.roleUnitSecretarySubtitle' },
      { value: 'ANCILLARY_STAFF', key: 'settings.rolesPermissions.roleAncillaryStaffSubtitle' },
      { value: 'AUDITOR', key: 'settings.rolesPermissions.roleAuditorSubtitle' },
      { value: 'DEFAULT', key: 'settings.rolesPermissions.roleDefaultSubtitle' },
      { value: 'DONOR', key: 'settings.rolesPermissions.roleDonorSubtitle' },
      { value: 'ADMIN', key: 'settings.rolesPermissions.roleAdminSubtitle' },
      { value: 'PRODUCTION_SUPPORT', key: 'settings.rolesPermissions.roleProductionSupportSubtitle' },
      { value: 'PRODUCT_TEAM', key: 'settings.rolesPermissions.roleProductTeamSubtitle' },
    ],
  },
  // ── Report Status ───────────────────────────────────────────────────────
  reportStatus: {
    entries: [
      { value: 'overdue', key: 'feed.status.overdue' },
      { value: 'due-soon', key: 'feed.status.dueSoon' },
      { value: 'up-to-date', key: 'reports.statusUpToDate' },
    ],
  },
  // ── Milk Source Type (pools section: MOM/DONOR) ───
  // Same fixed value set as MilkSourceType (apps/tms-app-soho/.../milk/types),
  // reusing the existing mother/donor labels.
  milkSourceType: {
    entries: [
      { value: 'MOM', key: 'common.mother' },
      { value: 'DONOR', key: 'common.donor' },
    ],
  },
  // ── Container / Bottle Role (Traceability Report — PRIMARY/SUPPLEMENT) ──
  // Fixed zod enum (packages/tms-types/src/schemas/traceability.schema.ts).
  containerRole: {
    entries: [
      { value: 'PRIMARY', key: 'reports.traceability.role.primary' },
      { value: 'SUPPLEMENT', key: 'reports.traceability.role.supplement' },
    ],
  },
  // ── Milk Receivable Use Type (milk_receivables.use_type: PROCESS/DIRECT) ─
  milkReceivableUseType: {
    entries: [
      { value: 'PROCESS', key: 'inventory.useTypeProcess' },
      { value: 'DIRECT', key: 'inventory.useTypeDirect' },
    ],
  },
  // ── Milk Receivable Lifecycle Status (milk_receivables.status) ──────────
  receivableLifecycleStatus: {
    entries: [
      { value: 'RECEIVED', key: 'disposeArea.status.RECEIVED' },
      { value: 'PROCESSED', key: 'disposeArea.status.PROCESSED' },
      { value: 'DISCARDED', key: 'inventory.itemStatusDiscarded' },
      { value: 'EXPIRED', key: 'common.expired' },
    ],
  },
  // ── Milk Inventory Item Status (milk_inventory.status — bottle lifecycle) ─
  milkInventoryItemStatus: {
    entries: [
      { value: 'AVAILABLE', key: 'milk.statusAvailable' },
      { value: 'ALLOCATED', key: 'inventory.itemStatusAllocated' },
      { value: 'CONSUMED', key: 'disposeArea.status.CONSUMED' },
      { value: 'DISCARDED', key: 'inventory.itemStatusDiscarded' },
      { value: 'QUARANTINE', key: 'inventory.itemStatusQuarantine' },
      { value: 'DEPLETED', key: 'inventory.itemStatusDepleted' },
    ],
  },
  // ── Milk Batch Status, raw DB values (milk_batches.status — Traceability) ─
  // Maps every raw status literal seen in milk-batches.store.ts's
  // `mapApiStatusToUiStatus()` straight onto the existing milkBatchStatus
  // i18n keys, so the Dispose Area module shows the same labels as the
  // Milk Batches module without needing a separate normalisation step.
  milkBatchStatusRaw: {
    entries: [
      { value: 'APPROVED', key: 'milk.actionApproved' },
      { value: 'ALLOCATED', key: 'milk.actionApproved' },
      { value: 'CONSUMED', key: 'milk.actionApproved' },
      { value: 'FAILED', key: 'user.failed' },
      { value: 'DISCARDED', key: 'user.failed' },
      { value: 'DELETED', key: 'user.failed' },
      { value: 'RETEST', key: 'milk.actionRetest' },
      { value: 'REJECTED', key: 'donor.rejected' },
      { value: 'UNAPPROVED', key: 'milk.statusUnapproved' },
      { value: 'CREATED', key: 'milk.statusUnapproved' },
    ],
  },
  // ── Milk Pool Status, raw DB values (milk_pools.pool_status) ─
  // Maps every raw status literal seen in milk-pools.store.ts's
  // `mapApiPoolStatusToUiStatus()` straight onto the existing milkPoolStatus
  // i18n keys.
  milkPoolStatusRaw: {
    entries: [
      { value: 'OPEN', key: 'milk.statusPrep' },
      { value: 'PREP', key: 'milk.statusPrep' },
      { value: 'PENDING_PASTEURIZATION', key: 'milk.statusPrep' },
      { value: 'QUARANTINE', key: 'milk.statusQuarantined' },
      { value: 'QUARANTINED', key: 'milk.statusQuarantined' },
      { value: 'CLOSED', key: 'feed.statusCompleted' },
      { value: 'COMPLETED', key: 'feed.statusCompleted' },
    ],
  },
  // ── Feed-prep safety-check status (PrepareFeedView.vue hard-stop scan check) ─
  // Covers the raw unit_status/batch_status/status literals checked against
  // UNSAFE_STATUSES before an item can be used in a feed preparation.
  feedSafetyStatus: {
    entries: [
      { value: 'RECALLED', key: 'feed.status.recalled' },
      { value: 'QUARANTINE', key: 'milk.statusQuarantined' },
      { value: 'QUARANTINED', key: 'milk.statusQuarantined' },
      { value: 'DISPOSED', key: 'feed.status.disposed' },
      { value: 'DISCARDED', key: 'feed.status.disposed' },
      { value: 'WITHDRAWN', key: 'feed.status.withdrawn' },
    ],
  },
};

export function useEnumTranslation() {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const enums = createEnumTranslation(t, locale, enumConfig);

  // ── Allergen name translation ─────────────────────────────────────────
  /**
   * Map from English API allergen name → i18n key. Shared with AllergenTable.
   * Keyed on the EXACT string the `allergens_eu` table / API returns (see
   * `packages/tms-database/src/prisma/seeds/seeder.sql`), which is plural /
   * phrased differently from earlier UI copy in places (e.g. "Eggs" not
   * "Egg", "Soybeans" not "Soy", "Sesame seeds" not "Sesame") — both forms
   * are kept below (plus dev-mock-data aliases like "Cow Milk") so a lookup
   * never silently falls back to the raw untranslated API string.
   */
  const ALLERGEN_NAME_KEY_MAP: Record<string, string> = {
    'Celery':            'ui.overview.allergenCelery',
    'Cow Milk':          'ui.overview.allergenMilk',
    'Crustaceans':       'ui.overview.allergenCrustaceans',
    'Egg':               'ui.overview.allergenEgg',
    'Eggs':              'ui.overview.allergenEgg',
    'Fish':              'ui.overview.allergenFish',
    'Gluten':            'ui.overview.allergenGluten',
    'Lactose':           'ui.overview.allergenLactose',
    'Latex':             'ui.overview.allergenLatex',
    'Lupin':             'ui.overview.allergenLupin',
    'Milk':              'ui.overview.allergenMilk',
    'Molluscs':          'ui.overview.allergenMolluscs',
    'Mustard':           'ui.overview.allergenMustard',
    'Nuts':              'ui.overview.allergenNuts',
    'Peanut':            'ui.overview.allergenPeanut',
    'Peanuts':           'ui.overview.allergenPeanut',
    'Penicillin':        'ui.overview.allergenPenicillin',
    'Sesame':            'ui.overview.allergenSesame',
    'Sesame seeds':      'ui.overview.allergenSesame',
    'Shellfish':         'ui.overview.allergenShellfish',
    'Soy':               'ui.overview.allergenSoy',
    'Soya':              'ui.overview.allergenSoy',
    'Soybeans':          'ui.overview.allergenSoy',
    'Sulphites':         'ui.overview.allergenSulphites',
    'Sulphur Dioxide':   'ui.overview.allergenSulphurDioxide',
    'Tree Nuts':         'ui.overview.allergenTreeNuts',
    'Wheat':             'ui.overview.allergenWheat',
  };

  /** Translate an allergen name returned from the API, falling back to the raw name. */
  function getTranslatedAllergenName(name: string): string {
    const key = ALLERGEN_NAME_KEY_MAP[name];
    return key ? t(key) : name;
  }

  return {
    // User roles
    userRoleLabels: enums.userRole.labels,
    getUserRoleLabel: enums.userRole.getLabel,
    userRoleOptions: enums.userRole.options,

    // User statuses
    userStatusLabels: enums.userStatus.labels,
    getUserStatusLabel: enums.userStatus.getLabel,

    // Patient statuses
    patientStatusLabels: enums.patientStatus.labels,
    getPatientStatusLabel: enums.patientStatus.getLabel,
    patientStatusOptions: enums.patientStatus.options,

    // Milk types
    milkTypeLabels: enums.milkType.labels,
    getMilkTypeLabel: enums.milkType.getLabel,
    milkTypeOptions: enums.milkType.options,

    // Milk statuses
    milkStatusLabels: enums.milkStatus.labels,
    getMilkStatusLabel: enums.milkStatus.getLabel,
    milkStatusOptions: enums.milkStatus.options,

    // Milk storage type (milk formula storage settings step)
    getMilkStorageTypeLabel: enums.milkStorageType.getLabel,
    milkStorageTypeOptions: enums.milkStorageType.options,

    // Formula storage type (milk formula storage settings step)
    getFormulaStorageTypeLabel: enums.formulaStorageType.getLabel,
    formulaStorageTypeOptions: enums.formulaStorageType.options,

    // Donor statuses
    donorStatusLabels: enums.donorStatus.labels,
    getDonorStatusLabel: enums.donorStatus.getLabel,
    donorStatusOptions: enums.donorStatus.options,
    // Filter subset: Approved, Cleared, On Hold only
    donorStatusFilterOptions: enums.donorStatusFilter.options,

    // Feed order types
    feedOrderTypeLabels: enums.feedOrderType.labels,
    getFeedOrderTypeLabel: enums.feedOrderType.getLabel,
    feedOrderTypeOptions: enums.feedOrderType.options,

    // Feed statuses (labels from full set, options from filter subset)
    feedStatusLabels: enums.feedStatus.labels,
    getFeedStatusLabel: enums.feedStatus.getLabel,
    feedStatusOptions: enums.feedStatusFilter.options,

    // Milk origin
    milkOriginLabels: enums.milkOrigin.labels,
    getMilkOriginLabel: enums.milkOrigin.getLabel,
    milkOriginOptions: enums.milkOrigin.options,

    // Child status (mother)
    childStatusOptions: enums.childStatus.options,

    // Alcohol use (mother)
    alcoholUseOptions: enums.alcoholUse.options,

    // Diet type (mother)
    dietTypeOptions: enums.dietType.options,

    // Mother role
    motherRoleOptions: enums.motherRole.options,

    // Donor role
    donorRoleOptions: enums.donorRole.options,

    // Donor approval filter status
    donorApprovalFilterOptions: enums.donorApprovalFilter.options,

    // Milk batch status
    milkBatchStatusLabels: enums.milkBatchStatus.labels,
    getMilkBatchStatusLabel: enums.milkBatchStatus.getLabel,
    milkBatchStatusOptions: enums.milkBatchStatus.options,
    // Filter subset: Unapproved, Approved, Retest, Rejected (no Failed)
    milkBatchStatusFilterOptions: enums.milkBatchStatusFilter.options,

    // Milk pool / pasteurization status
    milkPoolStatusLabels: enums.milkPoolStatus.labels,
    getMilkPoolStatusLabel: enums.milkPoolStatus.getLabel,
    milkPoolStatusOptions: enums.milkPoolStatus.options,

    // Batch approval action
    batchApprovalActionOptions: enums.batchApprovalAction.options,

    // Product status (inventory)
    productStatusOptions: enums.productStatus.options,

    // Recall status
    recallStatusLabels: enums.recallStatus.labels,
    getRecallStatusLabel: enums.recallStatus.getLabel,
    recallStatusOptions: enums.recallStatus.options,

    // Recall relationship
    getRelationshipLabel: enums.relationship.getLabel,
    relationshipOptions: enums.relationship.options,

    // Sex options
    sexOptions: enums.sex.options,

    // Patient communication (channel / type / recipient type / status)
    getPatientCommunicationChannelLabel: enums.patientCommunicationChannel.getLabel,
    patientCommunicationChannelOptions: enums.patientCommunicationChannel.options,
    getPatientCommunicationTypeLabel: enums.patientCommunicationType.getLabel,
    patientCommunicationTypeOptions: enums.patientCommunicationType.options,
    getPatientCommunicationRecipientTypeLabel: enums.patientCommunicationRecipientType.getLabel,
    patientCommunicationRecipientTypeOptions: enums.patientCommunicationRecipientType.options,
    getPatientCommunicationStatusLabel: enums.patientCommunicationStatus.getLabel,
    patientCommunicationStatusOptions: enums.patientCommunicationStatus.options,

    // Allergen severity
    allergenSeverityLabels: enums.allergenSeverity.labels,
    getAllergenSeverityLabel: enums.allergenSeverity.getLabel,
    allergenSeverityOptions: enums.allergenSeverity.options,

    // Allergen actions
    allergenActionOptions: enums.allergenAction.options,

    // Milk type status
    getMilkTypeStatusLabel: enums.milkTypeStatus.getLabel,
    milkTypeStatusOptions: enums.milkTypeStatus.options,

    // Facility type
    getFacilityTypeLabel: enums.facilityType.getLabel,
    facilityTypeOptions: enums.facilityType.options,

    // Care unit type
    getCareUnitTypeLabel: enums.careUnitType.getLabel,
    careUnitTypeOptions: enums.careUnitType.options,

    // Milk category (expiry rules)
    getMilkCategoryLabel: enums.milkCategory.getLabel,
    milkCategoryOptions: enums.milkCategory.options,

    // Milk state (expiry rules)
    getMilkStateLabel: enums.milkState.getLabel,
    milkStateOptions: enums.milkState.options,

    // Storage state — "State" column (expiry rules milk table)
    getExpiryStorageStateLabel: enums.expiryStorageState.getLabel,
    expiryStorageStateOptions: enums.expiryStorageState.options,

    // Physical location — "Location" column (expiry rules milk + feed tables)
    getExpiryLocationLabel: enums.expiryLocation.getLabel,
    expiryLocationOptions: enums.expiryLocation.options,

    // Device type / status (Printer Config settings)
    getDeviceTypeLabel: enums.deviceType.getLabel,
    deviceTypeOptions: enums.deviceType.options,
    getDeviceStatusLabel: enums.deviceStatus.getLabel,
    deviceStatusOptions: enums.deviceStatus.options,

    // Formula form factor — "Form Factor" column (expiry rules formula table)
    getFormulaFormFactorLabel: enums.formulaFormFactor.getLabel,
    formulaFormFactorOptions: enums.formulaFormFactor.options,

    // Product form (form_name) — product catalog / feed-order / traceability APIs
    getProductFormLabel: enums.productForm.getLabel,
    productFormOptions: enums.productForm.options,

    // Milk storage condition (expiry rules milk table)
    getMilkStorageConditionLabel: enums.milkStorageCondition.getLabel,
    milkStorageConditionOptions: enums.milkStorageCondition.options,

    // Feed expiry type (expiry rules feed table)
    getFeedExpiryTypeLabel: enums.feedExpiryType.getLabel,
    feedExpiryTypeOptions: enums.feedExpiryType.options,

    // Expiry unit
    getExpiryUnitLabel: enums.expiryUnit.getLabel,
    expiryUnitOptions: enums.expiryUnit.options,

    // Settings role names (permission matrix)
    getSettingsRoleLabel: enums.settingsRole.getLabel,

    // Settings role subtitles (permission matrix)
    getSettingsRoleSubtitleLabel: enums.settingsRoleSubtitle.getLabel,

    // Report status
    reportStatusLabels: enums.reportStatus.labels,
    getReportStatusLabel: enums.reportStatus.getLabel,
    reportStatusOptions: enums.reportStatus.options,

    // Milk source type (used by Dispose Area pools section: MOM/DONOR)
    getMilkSourceTypeLabel: enums.milkSourceType.getLabel,

    // Milk batch / pool status from raw DB values.
    // (e.g. "Approved"/"Prep" in some places, "APPROVED"/"PREP" in others),
    // but every entry in the milkBatchStatusRaw/milkPoolStatusRaw maps is keyed uppercase.
    getMilkBatchStatusRawLabel: (value: string) => enums.milkBatchStatusRaw.getLabel(value?.toUpperCase() ?? value),
    getMilkPoolStatusRawLabel: (value: string) => enums.milkPoolStatusRaw.getLabel(value?.toUpperCase() ?? value),

    // Feed-prep safety-check status (RECALLED/QUARANTINE/DISPOSED/WITHDRAWN/...)
    getFeedSafetyStatusLabel: (value: string) => enums.feedSafetyStatus.getLabel(value?.toUpperCase() ?? value),

    // Generic
    translateEnum: enums.translateEnum,

    // Allergen name translation (for API-sourced allergen dropdowns)
    getTranslatedAllergenName,
  };
}
